import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle, Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { cn, getPeopleByEmail, getPeopleByFireauthId, updatePersonFireAuthId } from "../lib/utils";
import napcoLogo from "../assets/napco-logo.png";
import { signInWithEmail, signUpWithEmail, resetPassword, logout } from "../lib/auth";

const UserAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // mode can be: "login", "register", or "reset"
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Check if the current URL path contains /register or /reset-password
    if (location.pathname.includes('/register')) {
      setMode("register");
    } else if (location.pathname.includes('/reset-password')) {
      setMode("reset");
    } else {
      setMode("login");
    }
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromSession = sessionStorage.getItem('napco-inactive-redirect');
    if (params.get('error') === 'inactive' || fromSession) {
      setError('Your account is inactive. Please contact your administrator to restore access.');
      if (fromSession) sessionStorage.removeItem('napco-inactive-redirect');
    }
  }, [location.search]);

  /**
   * Check if a user exists in the People table by email and if they already have a fireauth_id
   * @param {string} email - The email to check
   * @returns {Promise<{exists: boolean, hasFireauthId: boolean, peopleRecord: object|null}>}
   */
  const checkUserExistsInPeopleTable = async (email) => {
    try {
      const response = await getPeopleByEmail(email);
      console.log("People API response:", response.data);
      
      const peopleRecords = response.data.peoples || [];
      const exists = peopleRecords.length > 0;
      
      // If user exists, check if they already have a fireauth_id
      if (exists) {
        const peopleRecord = peopleRecords[0];
        const hasFireauthId = !!peopleRecord.fireauth_id; // Convert to boolean
        
        return {
          exists,
          hasFireauthId,
          peopleRecord
        };
      }
      
      return {
        exists: false,
        hasFireauthId: false,
        peopleRecord: null
      };
    } catch (error) {
      console.error("Error checking user existence:", error);
      throw new Error("Failed to verify user eligibility. Please try again later.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (mode === "login") {
        // Login
        const firebaseUser = await signInWithEmail(email.trim(), password);

        // Verify the user has an Active People record before granting access.
        // Done inline (vs. relying on UserDataContext) so the inactive case
        // shows an inline error without triggering a redirect/refresh.
        // Looked up by Firebase UID — that's the stable session join key.
        let personRecord;
        try {
          const byUid = await getPeopleByFireauthId(firebaseUser.uid);
          personRecord = byUid.data.peoples?.[0];

          // First-login stamp: if no record matches the UID, the user's row
          // exists but hasn't been stamped yet (e.g., admin added them and
          // the stamp never landed). Look up by email and stamp the UID.
          if (!personRecord) {
            const byEmail = await getPeopleByEmail(email.trim());
            const emailRecord = byEmail.data.peoples?.[0];
            if (emailRecord && !emailRecord.fireauth_id) {
              await updatePersonFireAuthId(emailRecord, firebaseUser.uid);
              personRecord = { ...emailRecord, fireauth_id: firebaseUser.uid };
            }
            // If emailRecord has a different fireauth_id, leave personRecord
            // null — this Firebase account is bound to someone else's row.
          }
        } catch (statusError) {
          console.error('Error verifying account status:', statusError);
          await logout();
          setError('Failed to verify your account. Please try again later.');
          return;
        }

        if (!personRecord || personRecord.status !== 'Active') {
          await logout();
          setError('Your account is inactive. Please contact your administrator to restore access.');
          return;
        }

        // After successful login, navigate to root
        navigate('/');
      } else if (mode === "register") {
        // Registration
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        
        try {
          // First, check if the user exists in the People table and if they already have a fireauth_id
          console.log("Checking if user exists in People table:", email);
          const { exists, hasFireauthId, peopleRecord } = await checkUserExistsInPeopleTable(email);
          console.log("User exists in People table:", exists);
          console.log("User already has fireauth_id:", hasFireauthId);
          
          if (!exists) {
            setError("Your email is not registered in our system. Please contact a NAPCO administrator to add you first.");
            setLoading(false);
            return; // Stop the registration process
          }
          
          if (hasFireauthId) {
            setError(
              <span>
                An account with this email already exists. Please{" "}
                <button
                  type="button"
                  onClick={() => {
                    navigate('/login');
                    setError("");
                    setSuccess("");
                  }}
                  className="text-accent-600 hover:text-accent-800 font-medium"
                >
                  log in here
                </button>
                {" "}or{" "}
                <button
                  type="button"
                  onClick={() => {
                    navigate('/reset-password');
                    setError("");
                    setSuccess("");
                  }}
                  className="text-accent-600 hover:text-accent-800 font-medium"
                >
                  reset your password
                </button>
                {" "}if needed.
              </span>
            );
            setLoading(false);
            return; // Stop the registration process
          }
          
          // If user exists in People table and doesn't have a fireauth_id, proceed with Firebase registration
          console.log("User exists in People table and doesn't have a fireauth_id, proceeding with Firebase registration");
          const userCredential = await signUpWithEmail(email.trim(), password);
          
          // After successful registration, update the fireauth_id in Dataverse
          if (userCredential && userCredential.uid) {
            console.log("User registered successfully, updating fireauth_id in Dataverse");
            try {
              await updatePersonFireAuthId(peopleRecord, userCredential.uid);
              console.log("Updated fireauth_id in Dataverse successfully");
            } catch (updateError) {
              console.error("Error updating fireauth_id in Dataverse:", updateError);
              // Continue with navigation even if update fails - user is registered but fireauth_id update failed
              // In a production environment, you might want to handle this differently
            }
          }
          
          // After successful registration and auto-login, navigate to root
          navigate('/');
        } catch (peopleCheckError) {
          console.error("Error during people check:", peopleCheckError);
          setError(peopleCheckError.message || "Failed to verify user eligibility. Please try again later.");
          setLoading(false);
          return; // Stop the registration process
        }
      } else if (mode === "reset") {
        // Password Reset
        await resetPassword(email.trim());
        setSuccess(
          "If the email address you entered matches an existing account, you will receive an email within 10 minutes with instructions on how to reset your password."
        );
      }
    } catch (err) {
      // Log the error for debugging
      console.error("Auth Error:", err.code, err.message);
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Firebase errors by mapping specific error codes to user-friendly messages.
   */
  const handleError = (err) => {
    switch (err.code) {
      case "auth/user-not-found":
        // Email doesn't exist in Firebase
        setError("No account found with this email. Please register first.");
        break;
      case "auth/wrong-password":
      case "auth/invalid-credential":
      case "auth/invalid-login-credentials":
        // Handle all password/credential related errors with a simpler message
        setError(
          <span>
            Invalid username or password. If you forgot your password, please{" "}
            <button
              type="button"
              onClick={() => {
                navigate('/reset-password');
                setError("");
                setSuccess("");
              }}
              className="text-accent-600 hover:text-accent-800 font-medium"
            >
              reset it here
            </button>
            .
          </span>
        );
        break;
      case "auth/too-many-requests":
        setError("Too many failed attempts. Please try again later.");
        break;
      case "auth/invalid-email":
        setError("Invalid email format.");
        break;
      case "auth/weak-password":
        setError("Password is too weak. Please use a stronger password.");
        break;
      case "auth/email-already-in-use":
        setError(
          <span>
            Oops! An account with that email address already exists. Please{" "}
            <button
              type="button"
              onClick={() => {
                navigate('/login');
                setError("");
                setSuccess("");
              }}
              className="text-accent-600 hover:text-accent-800 font-medium"
            >
              log in here
            </button>
            {" "}or{" "}
            <button
              type="button"
              onClick={() => {
                navigate('/reset-password');
                setError("");
                setSuccess("");
              }}
              className="text-accent-600 hover:text-accent-800 font-medium"
            >
              reset your password
            </button>
            {" "}if needed.
          </span>
        );
        break;
      default:
        if (err.message) {
          // Custom errors (like "Passwords do not match")
          setError(err.message);
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
    }
  };

  // Helper to render the correct heading based on 'mode'
  const renderHeading = () => {
    if (mode === "login") return "Log In";
    if (mode === "register") return "Register";
    return "Reset Password";
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gray-50">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <img 
            alt="NAPCO Portal" 
            src={napcoLogo} 
            className="h-12 w-auto mb-4"
          />
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">NAPCO TMS</h1>
            <p className="text-sm text-gray-500">
              {mode === "login" && "Sign in to access your account"}
              {mode === "register" && "Create your account to get started"}
              {mode === "reset" && "Reset your password"}
            </p>
          </div>
        </div>
        
        {/* Auth Form */}
        <div className="rounded-xl bg-white p-8 shadow-lg border border-gray-100">
          <h2 className="text-xl font-semibold mb-6 text-left text-gray-800">
            {renderHeading()}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-700 mb-1.5"
              >
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-4 text-gray-900
                    shadow-sm ring-1 ring-inset ring-gray-300 
                    placeholder:text-gray-400 
                    focus:ring-2 focus:ring-inset focus:ring-accent-600 
                    sm:text-sm"
                />
              </div>
            </div>
            
            {/* Password Field (not needed in reset mode) */}
            {mode !== "reset" && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  {mode === "register" ? "Create Password" : "Password"}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={
                      mode === "login" ? "current-password" : "new-password"
                    }
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-10 text-gray-900 
                      shadow-sm ring-1 ring-inset ring-gray-300 
                      placeholder:text-gray-400 
                      focus:ring-2 focus:ring-inset focus:ring-accent-600 
                      sm:text-sm"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}
            
            {/* Confirm Password (only for register mode) */}
            {mode === "register" && (
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-10 text-gray-900 
                      shadow-sm ring-1 ring-inset ring-gray-300 
                      placeholder:text-gray-400 
                      focus:ring-2 focus:ring-inset focus:ring-accent-600 
                      sm:text-sm"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}
            
            {/* Error & Success Messages */}
            {error && (
              <div className="mt-2 flex items-start space-x-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>{error}</div>
              </div>
            )}
            
            {success && (
              <div className="mt-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                {success}
              </div>
            )}
            
            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center items-center rounded-lg bg-accent-600 
                  px-4 py-3 text-sm font-semibold text-white 
                  shadow-sm hover:bg-accent-700 transition-colors
                  focus-visible:outline focus-visible:outline-2 
                  focus-visible:outline-offset-2 focus-visible:outline-accent-600
                  disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Please wait...
                  </span>
                ) : (
                  <span className="flex items-center">
                    {renderHeading()} <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                )}
              </button>
            </div>
          </form>
          
          {/* Links to switch modes */}
          <div className="mt-6 border-t border-gray-100 pt-4">
            {mode === "login" && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Don&apos;t have an account?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/register');
                      setError("");
                      setSuccess("");
                    }}
                    className="text-accent-600 hover:text-accent-800 font-medium"
                  >
                    Register
                  </button>
                </p>
                <p className="text-sm text-gray-600">
                  Forgot your password?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/reset-password');
                      setError("");
                      setSuccess("");
                    }}
                    className="text-accent-600 hover:text-accent-800 font-medium"
                  >
                    Reset here
                  </button>
                </p>
              </div>
            )}
            {mode === "register" && (
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    navigate('/login');
                    setError("");
                    setSuccess("");
                  }}
                  className="text-accent-600 hover:text-accent-800 font-medium"
                >
                  Log In
                </button>
              </p>
            )}
            {mode === "reset" && (
              <p className="text-sm text-gray-600">
                Remembered your password?{" "}
                <button
                  type="button"
                  onClick={() => {
                    navigate('/login');
                    setError("");
                    setSuccess("");
                  }}
                  className="text-accent-600 hover:text-accent-800 font-medium"
                >
                  Log In
                </button>
              </p>
            )}
          </div>
          
          <p className="mt-4 text-xs text-gray-500">
            Need help? Contact <span className="text-accent-600">support@napco.com</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserAuth;
